import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { LoadingStatus } from "../components/Shared/Modals/LoadingStatusModal/LoadingStatusModal";
import {
  OrganisationDataQueryFn,
  organisationDataQueryOptions
} from "../config/queryConfig";
import { useLoadingStatus } from "../context/LoadingStatusContext";
import { VenueGroupConfig } from "../types/VenueGroup";
import { VenueUserConfig } from "../types/VenueUser";
import { useAppContext } from "./useAppContext";

type OrganisationDataQueryResponse = {
  user?: VenueUserConfig;
  venueGroup?: VenueGroupConfig;
};

const toOrganisationData = (
  data: unknown
): OrganisationDataQueryResponse | undefined =>
  typeof data === "object" && data !== null && !Array.isArray(data)
    ? (data as OrganisationDataQueryResponse)
    : undefined;

/**
 * Loads organisation data once startup has completed.
 *
 * The query is gated on `enabled` (startup `status === "success"`) rather than
 * on the query key, so the cached entry survives the transition to success and
 * is reused instead of refetched. Timing/key defaults come from
 * `organisationDataQueryOptions`.
 */
export const useOrganisationDataQuery = (
  status: LoadingStatus,
  loadOrganisationData: OrganisationDataQueryFn
) => {
  const queryClient = useQueryClient();
  const { setVenueGroupConfig, setVenueUserConfig } = useAppContext();
  const { updateStage } = useLoadingStatus();
  const { queryKey } = organisationDataQueryOptions;

  const query = useQuery({
    ...organisationDataQueryOptions,
    queryFn: loadOrganisationData,
    enabled: status === "success",
    // Seed the first render from the persisted-then-restored cache so a
    // returning user's organisation data paints immediately instead of a
    // loading state. An empty cache (first-ever load) yields undefined,
    // preserving the genuine loading path.
    initialData: () => queryClient.getQueryData(queryKey),
    // The restored snapshot is not organisation-scoped (the key is stable), so
    // it may belong to a previously selected organisation. Paint from it, but
    // treat it as stale so the query always refetches and an organisation
    // switch is picked up straight away rather than up to staleTime later.
    // When the persister has already restored the entry, initialData is not
    // used, so the snapshot is invalidated on restore in queryClient.ts.
    initialDataUpdatedAt: 0
  });

  useEffect(() => {
    const data = toOrganisationData(query.data);
    if (status !== "success" || !data) {
      return;
    }

    if (data.user) {
      setVenueUserConfig(data.user);
    }

    if (data.venueGroup?._id) {
      setVenueGroupConfig(data.venueGroup);
    }

    updateStage("organisation", "success");
  }, [
    status,
    query.data,
    setVenueUserConfig,
    setVenueGroupConfig,
    updateStage
  ]);

  return query;
};
